// Thin wrappers around the shared /api/legal-documents/* routes in puminet5api.
// Routes are described in puminet5api/modules/legalDocument/legalDocument.js;
// the schema (legalDocuments, legalDocumentTypes, personSettings) is shared
// across betoni.online and betonijerry.fi — see CLAUDE.md "Legal / acceptance
// model" for the cross-site framing.

import { API_URL } from "src/utils/apiUrl";
import { captureError } from "src/utils/errorReporting";

// Document type identifiers — matches legalDocumentTypes.typeName rows seeded
// by puminet5api/scripts/sql/add-legalDocumentTypes-personSettingTypeId-mapping.sql.
export const DOC_TYPES = {
    TOS: "BETONIJERRY_TOS",
    PRIVACY: "BETONIJERRY_PRIVACY",
};

async function jsonOrThrow(res) {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(body.message || `HTTP ${res.status}`);
        err.status = res.status;
        err.body = body;
        throw err;
    }
    return body;
}

function authHeaders(token) {
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * One retry on a network-level failure. `fetch` THROWS (Mobile Safari
 * "TypeError: Load failed", Chrome "Failed to fetch") when the connection drops
 * or the tab is backgrounded mid-request — a single transient that silently
 * downgrades the visitor to the bundled fallback copy and stamps
 * GATE1_FALLBACK_VERSION on their consent instead of the live DB version
 * (Sentry BETONIJERRY-1F).
 *
 * HTTP responses are deliberately NOT retried, whatever the status: a 404 means
 * "no active doc for this type", which useAcceptanceCopy handles as an expected
 * silent fallback, and retrying it would double the latency of the common case.
 */
async function fetchWithOneRetry(url) {
    try {
        return await fetch(url);
    } catch {
        await new Promise((resolve) => setTimeout(resolve, 400));
        return fetch(url);
    }
}

/**
 * Fetch the currently-active version of a document type. Public route (no auth
 * required). Retries once on a network-level failure — see fetchWithOneRetry.
 *
 * NOT tenant-scoped, and deliberately so: the typeName already carries the
 * tenant (BETONIJERRY_* rows are the betonijerry ones), and the backend route
 * looks documents up tenant-blind — it ignores any ownerAsiakasId the caller
 * sends. This used to pass `?ownerAsiakasId=1349`, which read as though the
 * lookup were scoped when nothing on either end honoured it. Don't re-add it:
 * the route is public and its cache key has no tenant dimension, so making the
 * backend trust a client-supplied owner id would be a cache-poisoning vector.
 *
 * Returns { documentId, version, title, markdownContent, typeName, displayName, ... }
 * or throws on non-2xx.
 */
export async function fetchActiveDocument(typeName, { language = "fi" } = {}) {
    const url = new URL(`${API_URL}/api/legal-documents/current/${encodeURIComponent(typeName)}`);
    url.searchParams.set("language", language);
    const res = await fetchWithOneRetry(url.toString());
    return jsonOrThrow(res);
}

/**
 * Record acceptance of one or more documents for the authenticated user.
 * `items` shape: [{ documentId, settingTypeId, version, typeName }]
 *
 * Wrapped in captureError + swallow so a backend failure doesn't break the
 * registration / login UX — the FE-side checkbox is the gate, the DB record is
 * the audit trail. If recording fails we want the user to still proceed and
 * the failure to surface in Sentry, not block their flow.
 */
export async function recordAcceptances({ personId, items, token }) {
    if (!personId || !Array.isArray(items) || items.length === 0) return { success: false };
    try {
        const res = await fetch(`${API_URL}/api/legal-documents/record-multiple-acceptances`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders(token) },
            body: JSON.stringify({ personId, acceptances: items }),
        });
        return await jsonOrThrow(res);
    } catch (err) {
        captureError(err, { tags: { entity: "legalDocument", operation: "recordAcceptances", swallowed: true } });
        return { success: false, error: err.message };
    }
}

/**
 * Re-record the user's BETONIJERRY_TOS acceptance after any successful
 * authentication. The privacy notice is information (GDPR Art. 13/14) and is
 * never recorded as accepted (cl#3767). Returning users get silent
 * re-affirmation on every login, which keeps personSettings in sync as the
 * terms evolve. Failures are reported to Sentry and swallowed so we never
 * block the auth flow.
 *
 * Used by every successful-login callsite (password, OAuth Google/MS/LinkedIn,
 * email-code, post-reset login).
 */
export async function reaffirmAcceptances(authData) {
    const personId = authData?.person?.[0]?.personId;
    if (!personId) return;
    let tos;
    try {
        tos = await fetchActiveDocument(DOC_TYPES.TOS);
    } catch (err) {
        captureError(err, { tags: { entity: "legalDocument", typeName: DOC_TYPES.TOS, swallowed: true } });
        return;
    }
    if (!tos?.documentId || !tos.version) return;
    recordAcceptances({
        personId,
        token: authData.token,
        items: [{
            documentId: tos.documentId,
            settingTypeId: 47, // BETONIJERRY_TOS_ACCEPTANCE — see legalDocumentTypes.personSettingTypeId
            version: tos.version,
            typeName: DOC_TYPES.TOS,
        }],
    });
}
